import log from 'loglevel';

import moment from 'moment';
import { put, takeEvery, call, select } from 'redux-saga/effects';
import {
  DATA_SUCCESS,
  DATA_SUBMIT,
  DATA_FAILURE,
  SITE_DATA_SUCCESS,
  SITE_DATA_SUBMIT,
  SITE_DATA_FAILURE,
  SITE_HOURLY_FORECAST_SUBMIT,
  SITE_HOURLY_FORECAST_SUCCESS,
  SITE_HOURLY_FORECAST_FAILURE,
  SITE_OBSERVATIONS_SUBMIT,
  SITE_OBSERVATIONS_SUCCESS,
  SITE_OBSERVATIONS_FAILURE,
  SITES_SUCCESS,
  SITES_SUBMIT,
  SITES_FAILURE,
} from './DataDuck.js';
import { AUTH_EXPIRED_COUNTDOWN } from "Login/LoginDuck";
import { ACTIONS } from 'Map/MapActions';
import { SUBSCRIPTIONDATA_SUBMIT } from './SubscriptionDuck'
import { UPDATE_WINTER_TOGGLE, UPDATE_UNIT_TOGGLE } from 'common/dataRetrieval/CompanyDuck';
import { THREATLIST_APPLY_FILTER } from 'ThreatList/ThreatListDuck';
import fetchApi, { checkStatus, parseJSON, errorConverter } from 'utils/ajax';

const trim = s => (s ? s.trim() : undefined);

const normalizedSites = sites =>
  sites.map(site => ({
    ...site,
    name: trim(site.name),
    address1: trim(site.address1),
    address2: trim(site.address2),
    city: trim(site.city),
    state: trim(site.state),
    zipCode: trim(site.zipCode)
  }));

function* fetchSitesCall(reqCompanyId) {
  try {
    const login = yield select(state => state.login);
    const authToken = login.authToken;

    const companyId = reqCompanyId ? reqCompanyId : (login.companyId ? login.companyId : undefined);

    if (!companyId) {
      log.error('DATA_SUBMIT without companyId!');
      return [];
    }

    const sitesQuery = `?filter[where][companyId]=${companyId}`;

    const sites = yield call(fetchApi, `/Sites${sitesQuery}`, {
      authToken,
      method: 'GET',
    });
    yield call(checkStatus, sites);
    const sitesParsedJson = yield call(parseJSON, sites);

    return {
      sites: normalizedSites(sitesParsedJson)
    };
  } catch (error) {
    log.warn('DATA_FAILURE', error.message);
    const code = error.response && error.response.status;
    let convertedError;
    if (code >= 400 && code < 500) {
      const parsedJson = yield call(parseJSON, error.response);
      convertedError = errorConverter(parsedJson.error);
    } else {
      convertedError = errorConverter({ statusCode: code });
    }

    if(convertedError.authExpired) yield put({ type: AUTH_EXPIRED_COUNTDOWN });
    yield put({
      type: DATA_FAILURE,
      message: convertedError._error ? convertedError._error : error.message
    });
  }
}

function* fetchSitesForCompany({companyId}) {
  try {
    const login = yield select(state => state.login);
    const authToken = login.authToken;

    //log.debug('fetchSitesForCompany', companyId)
    const sitesQuery = `?filter[where][companyId]=${companyId}`;
    const sites = yield call(fetchApi, `/Sites${sitesQuery}`, {
      authToken,
      method: 'GET',
    });
    yield call(checkStatus, sites);
    const sitesParsedJson = yield call(parseJSON, sites);

    yield put({
      type: SITES_SUCCESS,
      sites: sitesParsedJson,
      companyId
    });

    yield put({
      type: "COMPANYINFOS_SET_ACTIVESITECOUNT",
      companyId,
      activeSiteCount: sitesParsedJson?.filter(s => s.active)?.length
    })

  } catch (error) {
    log.warn('SITES_FAILURE', error.message);
    const code = error.response && error.response.status;
    let convertedError;
    if (code >= 400 && code < 500) {
      const parsedJson = yield call(parseJSON, error.response);
      convertedError = errorConverter(parsedJson.error);
    } else {
      convertedError = errorConverter({ statusCode: code });
    }

    log.debug('fetchSitesForCompany error', convertedError);
    if(convertedError.authExpired) {
      yield put({ type: AUTH_EXPIRED_COUNTDOWN });
    }
    yield put({
      type: SITES_FAILURE,
      message: convertedError._error ? convertedError._error : error.message
    });
  }
}

function* fetchRelatedDataCall() {
  try {
    const login = yield select(state => state.login);
    const authToken = login.authToken;

    // fetch related data only if something is selected in company selector (heavy call)
    const companySelector = yield select(state => state.companySelector);
    const companyId = login.companyId
      ? login.companyId
      : companySelector.companyId
        ? companySelector.companyId
        : undefined;

    if (!companyId)
      return {
        forecasts: [],
        threats: [],
        events: [],
        advisories: [],
        notifications: [],
        observations: [],
        siteConvectiveOutlooks: [],
        convectiveOutlooks: [],
      };

    const nowPlus71 = moment().add(71, 'hours').toISOString();
    const byCompanyQuery = companyId
      ? {
          where: {
            companyId: companyId,
            startDate: {
              lt: nowPlus71
            }
          }
        }
      : {};

    const summaryForecasts = yield call(fetchApi, `/Forecasts/summaries?companyId=${companyId}`, {
      authToken,
      method: 'GET',
    });
    yield call(checkStatus, summaryForecasts);
    const summaryForecastsParsedJson = yield call(parseJSON, summaryForecasts);

    const threats = yield call(fetchApi, '/Threats', {
      authToken,
      method: 'GET',
      query: {
        filter: JSON.stringify(byCompanyQuery)
      }
    });
    yield call(checkStatus, threats);
    const threatsParsedJson = yield call(parseJSON, threats);

    // Sorting threats wrt dates
    threatsParsedJson.sort(
      (first, sec) => new Date(first.startDate) - new Date(sec.startDate)
    );

    const events = yield call(fetchApi, '/Events', {
      authToken,
      method: 'GET',
      query: {
        filter: JSON.stringify(byCompanyQuery)
      }
    });
    yield call(checkStatus, events);
    const eventsParsedJson = yield call(parseJSON, events);

    // Sorting events wrt dates
    eventsParsedJson.sort(
      (first, sec) => new Date(first.startDate) - new Date(sec.startDate)
    );

    const advisories = yield call(fetchApi, '/Advisories', {
      authToken,
      method: 'GET',
      query: {
        filter: JSON.stringify(byCompanyQuery)
      }
    });
    yield call(checkStatus, advisories);
    const advisoriesParsedJson = yield call(parseJSON, advisories);

    const notificationsurl = companyId
      ? `/Companies/${companyId}/notifications`
      : '/Notifications';
    const notifications = yield call(fetchApi, notificationsurl, {
      authToken,
      method: 'GET',
      query: {
        filter: JSON.stringify(byCompanyQuery)
      }
    });
    yield call(checkStatus, notifications);
    const notificationsParsedJson = yield call(parseJSON, notifications);

    const advisoryTypes = yield call(fetchApi, '/Advisories/types', {
      authToken,
      method: 'GET',
      query: {}
    });
    yield call(checkStatus, advisoryTypes);
    const advisoryTypesParsedJson = yield call(parseJSON, advisoryTypes);

    const outlookData = yield call(fetchApi, `/ConvectiveOutlooks/byCompanyId?companyId=${companyId}`, {
      authToken,
      method: 'GET',
    })
    yield call(checkStatus, outlookData);
    const convectiveOutlooks = yield call(parseJSON, outlookData);

    return {
      forecasts: summaryForecastsParsedJson,
      threats: threatsParsedJson,
      events: eventsParsedJson,
      advisories: advisoriesParsedJson,
      notifications: notificationsParsedJson,
      advisoryTypes : advisoryTypesParsedJson && advisoryTypesParsedJson.result && advisoryTypesParsedJson.result.types ? advisoryTypesParsedJson.result.types : [],
      convectiveOutlooks
    };
  } catch (error) {
    log.warn('DATA_FAILURE', error.message);
    const code = error.response && error.response.status;
    let convertedError;
    if (code >= 400 && code < 500) {
      try {
      const parsedJson = yield call(parseJSON, error.response);
      convertedError = errorConverter(parsedJson.error);
      } catch (parseErr) {
        log.error('Parsing error.response as JSON failed:', parseErr);
        convertedError = {message: parseErr};
      }
    } else {
      convertedError = errorConverter({ statusCode: code });
    }

    log.warn('fetchDataSaga error', convertedError);
    if(convertedError.authExpired) yield put({ type: AUTH_EXPIRED_COUNTDOWN });
    yield put({
      type: DATA_FAILURE,
      message: convertedError._error ? convertedError._error : error.message
    });
  }
}

export function* fetchSitesBasicInformation({companyId}) {
  try {
    const login = yield select(state => state.login);
    const authToken = login.authToken;

    yield put({
      type: SUBSCRIPTIONDATA_SUBMIT, companyId
    });

    const sitesBasicInformation = yield call(
      fetchApi,
      `/Sites/basicInformation?companyId=${companyId}`,
      {
        authToken,
        method: 'GET'
      }
    );
    yield call(checkStatus, sitesBasicInformation);
    const sitesBasicInformationParsedJson = yield call(parseJSON, sitesBasicInformation);

    yield put({
      type: SITE_DATA_SUCCESS,
      payload: { ...sitesBasicInformationParsedJson }
    });

    yield put({
      type: DATA_SUBMIT,
      companyId
    });
  } catch (error) {
    log.warn('SITE_BASIC_DATA', error);
    const code = error.response.status;
    let convertedError;
    if (code >= 400 && code < 500) {
      const parsedJson = yield call(parseJSON, error.response);
      convertedError = errorConverter(parsedJson.error);
    } else {
      convertedError = errorConverter({ statusCode: code });
    }
    log.debug('fetchSitesBasicInformation error', convertedError);
    if(convertedError.authExpired) yield put({ type: AUTH_EXPIRED_COUNTDOWN });
    yield put({
      type: SITE_DATA_FAILURE,
      message: convertedError._error ? convertedError._error : error.message
    });
  }
}

export function* fetchSites({companyId}) {
  const sites = yield call(fetchSitesCall, companyId);

  // subscription data must be known before UPDATE_WINTER_TOGGLE
  yield put({
    type: SUBSCRIPTIONDATA_SUBMIT, companyId
  });

  const relatedData = yield call(fetchRelatedDataCall);

  if (relatedData){
    // Instructing middleware to dispatch corresponding action.
    yield put({
      type: DATA_SUCCESS,
      payload: { ...sites, ...relatedData, companyId }
    });

    yield put({
      type: UPDATE_WINTER_TOGGLE, companyId
    });

    const login = yield select(state => state.login);
    if(login.reportUnit) {
      yield put({
        type: UPDATE_UNIT_TOGGLE, preferredUnit: login.reportUnit
      });
    } else {
      yield put({
        type: UPDATE_UNIT_TOGGLE, companyId
      });
    }

    // populate threatlist.visibleSites
    yield put({
      type: THREATLIST_APPLY_FILTER
    });

  } else
    yield;
}

// TODO: refactor/rename: it's not just for hourly forecasts anymore
function* fetchSitesHourlyForecastsCall({ authToken, siteId }) {
  try {
    const login = yield select(state => state.login);
    if (!authToken) authToken = login.authToken;

    const nowPlus71 = moment().add(71, 'hours').toISOString();
    const hourlyForecasts = yield call(
      fetchApi,
      `/Forecasts?filter[where][siteId]=${siteId}&filter[where][date][lt]=${nowPlus71}`,
      {
        authToken,
        method: 'GET'
      }
    );
    yield call(checkStatus, hourlyForecasts);
    let forecastsParsedJson = yield call(parseJSON, hourlyForecasts);
    forecastsParsedJson.sort((a, b) => a?.date?.localeCompare(b?.date));

    return {
      siteId,
      siteHourlyForecasts: forecastsParsedJson.filter(f => f.timeSpanH === 1).sort((a, b) => a.date.localeCompare(b.date)),
      sitePeriodForecasts: forecastsParsedJson.filter(f => f.timeSpanH !== 1).sort((a, b) => a.date.localeCompare(b.date)),
    };
  } catch (error) {
    log.warn('SITE_HOURLY_FORECAST_FAILURE', error);
    const code = error.response.status;
    let convertedError;
    if (code >= 400 && code < 500) {
      const parsedJson = yield call(parseJSON, error.response);
      convertedError = errorConverter(parsedJson.error);
    } else {
      convertedError = errorConverter({ statusCode: code });
    }
    log.debug('fetchSitesHourlyForecastsSaga error', convertedError);
    if(convertedError.authExpired) yield put({ type: AUTH_EXPIRED_COUNTDOWN });
    yield put({
      type: SITE_HOURLY_FORECAST_FAILURE,
      message: convertedError._error ? convertedError._error : error.message
    });
  }
}

// TODO: refactor/rename: it's not just for hourly forecasts anymore
export function* fetchSitesHourlyForecasts(action) {
  const response = yield call(fetchSitesHourlyForecastsCall, action.payload);

  // Instructing middleware to dispatch corresponding action.
  yield put({
    type: SITE_HOURLY_FORECAST_SUCCESS,
    payload: response
  });
}

// TODO: refactor/rename: it's not just for hourly forecasts anymore
function* fetchSelectedSiteHourlyForecasts(action) {
  if (action.payload.selectionType === "select"){
    yield put({
      type: SITE_HOURLY_FORECAST_SUCCESS,
      payload: yield call(fetchSitesHourlyForecastsCall, action.payload)
    });
  } else yield;
}

function* fetchSitesObservationsCall({ authToken, siteId }) {
  try {
    const login = yield select(state => state.login);
    if (!authToken) authToken = login.authToken;

    const observations = yield call(
      fetchApi,
      `/Observations?filter[where][siteId]=${siteId}`,
      {
        authToken,
        method: 'GET'
      }
    );
    yield call(checkStatus, observations);
    const observationsParsedJson = yield call(parseJSON, observations);

    return {
      observations: observationsParsedJson
    };
  } catch (error) {
    log.warn('SITE_OBSERVATIONS_FAILURE', error);
    const code = error.response.status;
    let convertedError;
    if (code >= 400 && code < 500) {
      const parsedJson = yield call(parseJSON, error.response);
      convertedError = errorConverter(parsedJson.error);
    } else {
      convertedError = errorConverter({ statusCode: code });
    }
    log.debug('fetchSiteObservations error', convertedError);
    if(convertedError.authExpired) yield put({ type: AUTH_EXPIRED_COUNTDOWN });
    yield put({
      type: SITE_OBSERVATIONS_FAILURE,
      message: convertedError._error ? convertedError._error : error.message
    });
  }
}
export function* fetchSitesObservations(action) {
  const response = yield call(fetchSitesObservationsCall, action.payload);

  yield put({
    type: SITE_OBSERVATIONS_SUCCESS,
    payload: response
  });
}

export default function* dataSaga() {
  yield takeEvery(SITES_SUBMIT, fetchSitesForCompany);
  yield takeEvery(DATA_SUBMIT, fetchSites);
  yield takeEvery(SITE_HOURLY_FORECAST_SUBMIT, fetchSitesHourlyForecasts);
  yield takeEvery(ACTIONS.SELECT_SITE, fetchSelectedSiteHourlyForecasts);
  yield takeEvery(SITE_DATA_SUBMIT, fetchSitesBasicInformation);
  yield takeEvery(SITE_OBSERVATIONS_SUBMIT, fetchSitesObservations);
}
